import { HistoryIcon } from 'lucide-react'
import { useState } from 'react'

import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui'
import { useToast } from '@/hooks'
import { useEdits, useProjectLoaders } from '@/modules/projects'
import { PendingNote } from '@/modules/projects/components/Workbench/PendingNote'
import type { Edit } from '@/modules/projects/stores/types'

interface EditHistoryProps {
  slug: string
  onUndo: (edits: Edit[]) => Promise<void>
}

// Every edit made on this project's statements, newest first, each with its own undo. lukita refuses
// an undo a later edit is in the way of, and says which one to take back first.
export const EditHistory = ({ slug, onUndo }: EditHistoryProps) => {
  const edits = useEdits()
  const { fetchEdits } = useProjectLoaders()
  const { errorToast } = useToast()
  const [loading, setLoading] = useState(false)

  const load = async (open: boolean) => {
    if (!open) return
    setLoading(true)
    try {
      await fetchEdits(slug)
    } catch (error) {
      errorToast('Could not load the history', error as Error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Popover onOpenChange={load}>
      <PopoverTrigger asChild>
        <Button theme="ghost" size="sm" data-edit-history>
          <HistoryIcon />
          History
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[26rem] p-0">
        <p className="border-b px-4 py-2.5 text-sm font-medium">
          Changes to these statements
        </p>
        <div className="max-h-[60vh] overflow-y-auto px-4">
          {loading && edits.length === 0 ? (
            <PendingNote text="Loading the history…" className="py-3" />
          ) : null}
          {!loading && edits.length === 0 ? (
            <p className="py-3 text-sm text-ink-subtle">No changes yet.</p>
          ) : null}
          <ul className="divide-y">
            {edits.map((edit) => (
              <HistoryEntry
                key={edit.id}
                edit={edit}
                onUndo={(chosen) => onUndo([chosen])}
              />
            ))}
          </ul>
        </div>
      </PopoverContent>
    </Popover>
  )
}

import { HistoryEntry } from './HistoryEntry'
